// Face recognition service using face-api.js
// Models are loaded from CDN or can be served from public folder

export interface FaceDescriptor {
  descriptor: Float32Array;
  detection: {
    x: number;
    y: number;
    width: number;
    height: number;
    score: number;
  };
}

export interface FaceRecognitionResult {
  faces: FaceDescriptor[];
  imageWidth: number;
  imageHeight: number;
}

class FaceRecognitionService {
  private modelsLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  /**
   * Load face-api.js models
   * Models can be loaded from CDN or local public folder
   */
  async loadModels(): Promise<void> {
    if (this.modelsLoaded) {
      return;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = (async () => {
      try {
        // Dynamically import face-api.js
        // Using CDN version - in production, you might want to install via npm
        const faceapi = await this.loadFaceAPI();
        
        // Load models from CDN or public folder
        const MODEL_URL = '/models'; // Serve models from public/models folder
        
        // Try to load from public folder first, fallback to CDN
        try {
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
          ]);
        } catch (error) {
          console.warn('Failed to load models from public folder, trying CDN...', error);
          // Fallback to CDN
          const CDN_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(CDN_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(CDN_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(CDN_URL),
          ]);
        }

        this.modelsLoaded = true;
        console.log('Face recognition models loaded successfully');
      } catch (error) {
        console.error('Error loading face recognition models:', error);
        this.loadingPromise = null;
        throw new Error('Failed to load face recognition models. Please refresh the page and try again.');
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Dynamically load face-api.js library
   */
  private async loadFaceAPI(): Promise<any> {
    // Check if already loaded
    if ((window as any).faceapi) {
      return (window as any).faceapi;
    }

    // Try to load from CDN
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.14/dist/face-api.min.js';
      script.onload = () => {
        if ((window as any).faceapi) {
          resolve((window as any).faceapi);
        } else {
          reject(new Error('face-api.js loaded but not available'));
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load face-api.js library'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Detect faces and extract descriptors from an image
   */
  async detectFaces(image: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement): Promise<FaceRecognitionResult> {
    if (!this.modelsLoaded) {
      await this.loadModels();
    }

    const faceapi = (window as any).faceapi;
    if (!faceapi) {
      throw new Error('Face recognition library not loaded');
    }

    // Detect faces with landmarks and descriptors
    const detections = await faceapi
      .detectAllFaces(image, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptors();

    if (detections.length === 0) {
      throw new Error('No face detected. Please ensure your face is clearly visible in the camera.');
    }

    if (detections.length > 1) {
      throw new Error('Multiple faces detected. Please ensure only one person is in the frame.');
    }

    const detection = detections[0];
    const imageWidth = image instanceof HTMLVideoElement ? image.videoWidth : image.width;
    const imageHeight = image instanceof HTMLVideoElement ? image.videoHeight : image.height;

    return {
      faces: [
        {
          descriptor: detection.descriptor,
          detection: {
            x: detection.detection.box.x,
            y: detection.detection.box.y,
            width: detection.detection.box.width,
            height: detection.detection.box.height,
            score: detection.detection.score,
          },
        },
      ],
      imageWidth,
      imageHeight,
    };
  }

  /**
   * Detect faces without descriptors (faster, for real-time detection)
   */
  async detectFacesFast(image: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement): Promise<FaceRecognitionResult> {
    if (!this.modelsLoaded) {
      await this.loadModels();
    }

    const faceapi = (window as any).faceapi;
    if (!faceapi) {
      throw new Error('Face recognition library not loaded');
    }

    // Detect faces only (no landmarks or descriptors for speed)
    const detections = await faceapi
      .detectAllFaces(image, new faceapi.TinyFaceDetectorOptions());

    const imageWidth = image instanceof HTMLVideoElement ? image.videoWidth : image.width;
    const imageHeight = image instanceof HTMLVideoElement ? image.videoHeight : image.height;

    return {
      faces: detections.map((detection: any) => ({
        descriptor: new Float32Array(0), // Empty for fast detection
        detection: {
          x: detection.box.x,
          y: detection.box.y,
          width: detection.box.width,
          height: detection.box.height,
          score: detection.score,
        },
      })),
      imageWidth,
      imageHeight,
    };
  }

  /**
   * Convert Float32Array descriptor to base64 string for storage
   */
  descriptorToBase64(descriptor: Float32Array): string {
    // Convert Float32Array to ArrayBuffer, then to base64
    const buffer = descriptor.buffer.slice(
      descriptor.byteOffset,
      descriptor.byteOffset + descriptor.byteLength
    );
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string back to Float32Array descriptor
   */
  base64ToDescriptor(base64: string): Float32Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Float32Array(bytes.buffer);
  }

  /**
   * Compare two face descriptors and return similarity score (0-1)
   */
  compareDescriptors(descriptor1: Float32Array, descriptor2: Float32Array): number {
    const faceapi = (window as any).faceapi;
    if (!faceapi) {
      throw new Error('Face recognition library not loaded');
    }

    // Calculate Euclidean distance
    const distance = faceapi.euclideanDistance(descriptor1, descriptor2);
    
    // Convert distance to similarity score (lower distance = higher similarity)
    // Typical threshold: distance < 0.6 means same person
    const similarity = 1 / (1 + distance);
    return similarity;
  }
}

// Export singleton instance
export const faceRecognitionService = new FaceRecognitionService();

